import React from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import ShieldIcon from '@mui/icons-material/Shield';

const HeroSection: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        pt: 8, // To account for fixed header
      }}
    >
      {/* Animated background elements */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '50%',
              width: `${Math.random() * 100 + 50}px`,
              height: `${Math.random() * 100 + 50}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        ))}
      </Box>

      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 6 }}>
          <Box sx={{ flex: 1 }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                }}
              >
                Интеллектуальная защита от читеров для игр на Unity
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  textShadow: '0 1px 5px rgba(0,0,0,0.2)',
                }}
              >
                Защитите свою игру, обеспечьте честный игровой процесс и сохраните свой доход
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <ScrollLink to="faq" smooth={true} duration={500}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                    }}
                  >
                    Начать
                  </Button>
                </ScrollLink>
                <ScrollLink to="features" smooth={true} duration={500}>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: '#fff',
                      '&:hover': {
                        borderColor: '#fff',
                        background: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Узнать больше
                  </Button>
                </ScrollLink>
              </Box>
            </motion.div>
          </Box>
          <Box sx={{ flex: 1 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '500px',
                }}
              >
                <motion.div
                  animate={{ 
                    rotateZ: [0, 360],
                  }}
                  transition={{ 
                    duration: 20,
                    repeat: Infinity,
                    ease: 'linear'
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '110%',
                    height: '110%',
                    borderRadius: '50%',
                    border: '4px solid rgba(255,255,255,0.1)',
                  }}
                />
                <motion.div
                  animate={{ 
                    rotateZ: [360, 0],
                  }}
                  transition={{ 
                    duration: 15,
                    repeat: Infinity,
                    ease: 'linear'
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    height: '90%',
                    borderRadius: '50%',
                    border: '4px solid rgba(255,255,255,0.15)',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '50%',
                    padding: '25%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2), inset 0 0 0 2px rgba(255,255,255,0.1)',
                  }}
                >
                  <ShieldIcon 
                    sx={{ 
                      fontSize: '10rem', 
                      color: '#fff', 
                      filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.3))'
                    }} 
                  />
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection; 